import React, {useEffect, useState} from 'react';
import {message} from 'antd';
import DirectoryManager from '../../components/DirectoryManager';
import {useTranslation} from '../../hooks/useTranslation';
import {isSystemDirectory, sortDirectories} from '../../utils/dirTree';
import './index.css';

const Directories: React.FC = () => {
  const {t} = useTranslation();
  const [dirLoading, setDirLoading] = useState(false);
  const [dirList, setDirList] = useState<string[]>([]);

  const formatDirLabel = (value: string) => {
    if (value === '__all__' || value === '全部目录') return t('common.allDirectories');
    if (value === '__default__' || value === '默认目录') return t('common.defaultDirectory');
    return value;
  };

  const fetchDirList = async () => {
    setDirLoading(true);
    try {
      const res = await fetch('/img-dir/list');
      const json = await res.json();
      if (json.success) {
        const dirs = sortDirectories(Array.isArray(json.data) ? json.data : []);
        setDirList(dirs);
        return dirs;
      }

      message.error(json.message || t('batchActions.getDirectoryFailed'));
    } catch (error) {
      message.error(t('batchActions.getDirectoryFailed'));
    } finally {
      setDirLoading(false);
    }

    return [];
  };

  const handleAddDirectory = async (dirName: string) => {
    setDirLoading(true);
    try {
      const res = await fetch('/img-dir/add', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({dirName}),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.message || t('batchActions.directoryAddFailed'));

      message.success(t('batchActions.directoryAdded'));
      await fetchDirList();
      return true;
    } catch (error: any) {
      message.error(error?.message || t('batchActions.directoryAddFailed'));
      return false;
    } finally {
      setDirLoading(false);
    }
  };

  const handleDeleteDirectory = async (dirName: string) => {
    if (!dirName) return false;

    if (isSystemDirectory(dirName)) {
      message.warning(t('batchActions.directoryCannotDelete', {dir: formatDirLabel(dirName)}));
      return false;
    }

    setDirLoading(true);
    try {
      const res = await fetch('/img-dir/delete', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({dirName}),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.message || t('batchActions.directoryDeleteFailed'));

      message.success(t('batchActions.directoryDeleted'));
      await fetchDirList();
      return true;
    } catch (error: any) {
      message.error(error?.message || t('batchActions.directoryDeleteFailed'));
      return false;
    } finally {
      setDirLoading(false);
    }
  };

  useEffect(() => {
    fetchDirList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="directories-page">
      <DirectoryManager
        directories={dirList}
        loading={dirLoading}
        onRefresh={fetchDirList}
        onAddDirectory={handleAddDirectory}
        onDeleteDirectory={handleDeleteDirectory}
      />
    </div>
  );
};

export default Directories;
